import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

export const MAX_ROWS = 10;

type TooltipOverflowListProps<TLine extends { id: string }> = {
    lines: readonly TLine[];
    /** i18n key receiving a `count` interpolation for the hidden remainder. */
    moreLabelKey: string;
    renderLine: (line: TLine) => ReactNode;
};

/** Renders tooltip rows capped at `MAX_ROWS`, followed by a "+N more" row for the remainder. */
export const TooltipOverflowList = <TLine extends { id: string }>({
    lines,
    moreLabelKey,
    renderLine,
}: TooltipOverflowListProps<TLine>) => {
    const { translate } = useTranslate();

    const visibleLines = lines.slice(0, MAX_ROWS);
    const overflowCount = lines.length - visibleLines.length;

    return (
        <>
            {visibleLines.map(renderLine)}
            {overflowCount > 0 ? (
                <Text
                    fontVariant="tooltip"
                    color={colorsPalettes.carbon[0]}
                >
                    {translate(moreLabelKey, { count: overflowCount })}
                </Text>
            ) : null}
        </>
    );
};
